'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Switch } from 'react-router-dom';
import TrackedRoute from 'AdsUI/lib/TrackedRoute';
import CampaignListManagementPanelContainer from 'AdsUI/containers/campaign-details/CampaignListManagementPanelContainer';
import { filtersIncludePurgedDates, getGlobalFilters } from 'AdsUI/selectors/GlobalFilters';
import { shouldFetchCampaignList, shouldFetchCampaignListEarlyRequest, getCampaignList, getCampaignListRequestStatus } from 'AdsUI/selectors/CampaignListSelectors';
import { getQueryStringFromFilters } from 'AdsUI/lib/GlobalFiltersQueryParams.js';
import { fetchCampaignList, fetchCampaignListEarlyRequest } from 'AdsUI/actions/CampaignListActions';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import DashboardNavigation from 'AdsUI/components/DashboardNavigation';
import DashboardNavigationItems from 'AdsUI/lib/DashboardNavigationItems';
import GenericErrorBoundary from 'AdsUI/components/GenericErrorBoundary';
import CampaignListPage from 'AdsUI/components/campaigns/CampaignListPage';
import { fetchAdsUserAttributes, saveCouponBannerHiddenForUser } from 'AdsUI/actions/UserAttributesActions';
import { fetchErrorAlert } from 'AdsUI/actions/LeadSyncingActions';
import { getOnboardingTourStatus } from 'AdsUI/selectors/UserAttributesSelectors';
import { getRequestStatus } from 'AdsUI/selectors/RequestStatusSelectors';
import HubAttributesActions from 'AdsUI/actions/HubAttributesActions';
import { getHubSpotUserId, hasUsersAndTeams } from 'AdsUI/selectors/AuthSelectors';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { hasSelectedConversionsColumn, getColumnsRequestStatus } from 'AdsUI/selectors/ColumnsSelectors';
import CampaignsSidebar from 'AdsUI/components/CampaignsSidebar';
import { fetchDrafts } from 'AdsUI/actions/DraftsActions';
import { getDraftsRequestStatus } from 'AdsUI/selectors/DraftsSelectors';
import { setupCsat } from 'wootric-nps/csat/csat-setup';
import { getTeamsInfoRequestStatus } from 'AdsUI/selectors/UsersAndTeamsSelectors';
import { withRouter } from 'react-router-dom';

const trackCacheStatus = cacheHit => {
  if (window.newrelic) {
    window.newrelic.addPageAction('ads-campaign-cache-event', {
      'ads-cache-hit': cacheHit,
      'ads-data-type': 'campaign-list'
    });
  }
};

class CampaignListPageContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.onHideCouponBanner = this.onHideCouponBanner.bind(this);
  }

  componentDidMount() {
    const {
      globalFilters,
      hubAttributesRequestStatus,
      userAttributesFetched,
      history,
      location
    } = this.props;

    if (globalFilters.initialized) {
      trackCacheStatus(!this.props.fetchCampaignListNeeded);
    }

    if (this.props.fetchCampaignListEarlyRequestNeeded) {
      this.props.fetchCampaignListEarlyRequest(globalFilters);
      history.replace(location.pathname + getQueryStringFromFilters(globalFilters));
    } else if (globalFilters.initialized) {
      history.replace(location.pathname + getQueryStringFromFilters(globalFilters));
    }

    if (!userAttributesFetched.fetched) {
      this.props.fetchAdsUserAttributes(this.props.userId);
    }

    if (!hubAttributesRequestStatus.fetchedSuccessfully() && !hubAttributesRequestStatus.fetching) {
      this.props.fetchHubAttributes();
    }

    this.props.fetchErrorAlert();
    setupCsat('ADS');
  }

  componentDidUpdate(prevProps) {
    if (this.props.fetchCampaignListNeeded) {
      this.props.fetchCampaignList(this.props.globalFilters);
    }

    if (this.props.fetchCampaignListEarlyRequestNeeded) {
      this.props.fetchCampaignListEarlyRequest(this.props.globalFilters);
    }

    if (prevProps.globalFilters !== this.props.globalFilters) {
      this.props.history.replace(this.props.location.pathname + getQueryStringFromFilters(this.props.globalFilters));
      trackCacheStatus(!this.props.fetchCampaignListNeeded);
    } // fetchDrafts depends on teamInfo response (not in early requester)


    if (this.props.hasAccessToUsersAndTeams && !this.props.teamsInfoRequestStatus.fetched) {
      return;
    }

    if (!this.props.draftsRequestStatus.fetchedSuccessfully() && !this.props.draftsRequestStatus.fetching) {
      this.props.fetchDrafts();
    }
  }

  onHideCouponBanner() {
    this.props.saveCouponBannerHiddenForUser(this.props.userId);
    usageTracker.track('couponAction', {
      action: 'Dismissed banner'
    });
  }

  render() {
    const {
      campaignList,
      campaignListRS,
      onboardingTourStatus,
      userAttributesFetched,
      globalFilters,
      hasAccessToUsersAndTeams,
      showLIBanner
    } = this.props;
    const showOnboardingWidget = userAttributesFetched.fetched && onboardingTourStatus === 'false';
    return /*#__PURE__*/_jsx(UIDashboardPage, {
      headerComponent: /*#__PURE__*/_jsx(DashboardNavigation, {
        activeItem: DashboardNavigationItems.PAID_CAMPAIGNS
      }),
      sidebarComponent: /*#__PURE__*/_jsx(CampaignsSidebar, {}),
      pageLayout: "full-width",
      children: /*#__PURE__*/_jsxs(GenericErrorBoundary, {
        children: [globalFilters.initialized && /*#__PURE__*/_jsx(CampaignListPage, {
          campaignList: campaignList,
          campaignListRS: campaignListRS,
          showOnboardingWidget: showOnboardingWidget,
          showLIBanner: showLIBanner,
          hasAccessToUsersAndTeams: hasAccessToUsersAndTeams
        }), /*#__PURE__*/_jsx(Switch, {
          children: /*#__PURE__*/_jsx(TrackedRoute, {
            path: "/manage/:network/:accountId/campaigns/:campaignId",
            component: CampaignListManagementPanelContainer,
            name: "campaign-enhanced-editor",
            exact: true
          })
        })]
      })
    });
  }

}

const mapStateToProps = state => ({
  globalFilters: getGlobalFilters(state),
  fetchCampaignListNeeded: shouldFetchCampaignList(state),
  fetchCampaignListEarlyRequestNeeded: shouldFetchCampaignListEarlyRequest(state),
  campaignList: getCampaignList(state),
  campaignListRS: getCampaignListRequestStatus(state),
  onboardingTourStatus: getOnboardingTourStatus(state),
  userAttributesFetched: getRequestStatus('userAttributes')(state),
  hubAttributesRequestStatus: getRequestStatus('hubAttributes')(state),
  userId: getHubSpotUserId(state),
  showConversionsFeedback: hasSelectedConversionsColumn(state),
  columnsLoaded: getColumnsRequestStatus(state).fetchedSuccessfully(),
  draftsRequestStatus: getDraftsRequestStatus(state),
  hasAccessToUsersAndTeams: hasUsersAndTeams(state),
  showLIBanner: filtersIncludePurgedDates(state),
  teamsInfoRequestStatus: getTeamsInfoRequestStatus(state)
});

export default connect(mapStateToProps, {
  fetchCampaignList,
  fetchCampaignListEarlyRequest,
  fetchAdsUserAttributes,
  fetchHubAttributes: HubAttributesActions.fetch,
  saveCouponBannerHiddenForUser,
  fetchDrafts,
  fetchErrorAlert
})(withRouter(CampaignListPageContainer));